import { motion } from "framer-motion";
import {
  Calendar,
  Clock,
  Edit2,
  Heart,
  ListMusic,
  MoreHorizontal,
  Play,
  Plus,
  Share2,
  Shuffle,
  Trash2,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AddTracksDialog } from "../components/AddTracksDialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PlaylistManager } from "../components/PlaylistManager";
import { TrackList } from "../components/TrackList";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { usePlayerStore } from "../stores/playerStore";
import { usePlaylistStore } from "../stores/playlistStore";
import { Track } from "../types";
import { formatTime } from "../utils/formatTime";

const convertSpotifyTrackToTrack = (item: any): Track => ({
  id: item.track.id,
  title: item.track.name,
  artist: item.track.artists[0].name,
  albumArt: item.track.album.images[0]?.url,
  duration: item.track.duration_ms / 1000,
  url: item.track.preview_url || "",
  source: "spotify" as const,
  spotifyData: item.track,
});

export const PlaylistDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { playlists, addTrackToPlaylist, removeTrackFromPlaylist, deletePlaylist } = usePlaylistStore();

  // Only find from playlists, not albums
  const playlist = playlists.find((p) => p.id === id);

  const { setCurrentTrack, setIsPlaying, setQueue } = usePlayerStore();
  const { isContentLiked, toggleLike } = useLocalLibraryStore();
  const [showAddTracks, setShowAddTracks] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const tracks: Track[] = useMemo(() => {
    if (playlist) {
      return playlist.tracks;
    }
    return [];
  }, [playlist]);

  if (!playlist) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold mb-4">Playlist not found</h1>
        <button onClick={() => navigate(-1)} className="text-yellow-500 hover:underline">
          Go back
        </button>
      </div>
    );
  }

  const totalDuration = tracks.reduce((acc, track) => acc + track.duration, 0);

  const handlePlay = () => {
    if (tracks.length > 0) {
      setCurrentTrack(tracks[0]);
      setQueue(tracks);
      setIsPlaying(true);
    }
  };

  const handleShuffle = () => {
    if (tracks.length > 0) {
      const shuffledTracks = [...tracks].sort(() => Math.random() - 0.5);
      setCurrentTrack(shuffledTracks[0]);
      setQueue(shuffledTracks);
      setIsPlaying(true);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: playlist.name,
        text: `Check out this playlist: ${playlist.name}`,
        url: window.location.href,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleDelete = () => {
    deletePlaylist(playlist.id);
    navigate("/playlists");
  };

  const handleAddTracks = (newTracks: Track[]) => {
    newTracks.forEach((track) => {
      addTrackToPlaylist(playlist.id, track);
    });
    setShowAddTracks(false);
  };

  const handleRemoveTrack = (trackId: string) => {
    removeTrackFromPlaylist(playlist.id, trackId);
  };

  const isLiked = isContentLiked(playlist.id, "playlist");
  const source = "local" as const;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-800/50 to-zinc-900">
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        {/* Playlist Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
          <div className="space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold">{playlist.name}</h1>
            {playlist.description && <p className="text-zinc-400">{playlist.description}</p>}
            <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>You</span>
              </div>
              <div className="flex items-center gap-2">
                <ListMusic className="w-4 h-4" />
                <span>{tracks.length} tracks</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{formatTime(totalDuration)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(playlist.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handlePlay}
              className="flex-1 md:flex-none px-8 py-3 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Play
            </button>
            <div className="flex items-center">
              <button onClick={handleShuffle} className="p-3 text-zinc-400 hover:text-white transition">
                <Shuffle className="w-5 h-5" />
              </button>
              <button
                onClick={() => toggleLike(playlist.id, "playlist", source)}
                className={`p-3 transition ${isLiked ? "text-yellow-500" : "text-zinc-400 hover:text-white"}`}
              >
                <Heart className="w-5 h-5" />
              </button>
              <button onClick={handleShare} className="p-3 text-zinc-400 hover:text-white transition">
                <Share2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowAddTracks(true)}
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-zinc-800 text-white rounded-full hover:bg-zinc-700 transition"
              >
                <Plus className="w-4 h-4" />
                Add Tracks
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-3 text-zinc-400 hover:text-white transition"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </button>

                {showMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-zinc-800 rounded-md shadow-lg z-50">
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setShowEditDialog(true);
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-700"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit Playlist
                    </button>
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setShowDeleteConfirm(true);
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-700 text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Playlist
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Add Tracks button */}
          <button
            onClick={() => setShowAddTracks(true)}
            className="md:hidden w-full px-4 py-3 bg-zinc-800 text-white rounded-full hover:bg-zinc-700 transition flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Tracks
          </button>
        </div>

        {/* Track List */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <TrackList
            tracks={tracks}
            onRemove={handleRemoveTrack}
            image={playlist.coverUrl}
            title={playlist.name}
            subtitle="You"
          />
        </motion.div>

        {/* Dialogs */}
        <AddTracksDialog
          isOpen={showAddTracks}
          onClose={() => setShowAddTracks(false)}
          onAddTracks={handleAddTracks}
          onRemoveTrack={handleRemoveTrack}
          existingTracks={tracks}
          title="Add Tracks to Playlist"
        />

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Playlist"
          message={`Are you sure you want to delete "${playlist.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          type="danger"
        />

        {showEditDialog && (
          <PlaylistManager playlistId={playlist.id} isOpen={true} onClose={() => setShowEditDialog(false)} />
        )}
      </div>
    </div>
  );
};
